import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import SearchIcon from '@material-ui/icons/Search';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useDebounce } from 'use-debounce';
import uuid4 from 'uuid4';

import GreenButton from '../../common/styled/GreenButton/GreenButton';
import CustomLoader from '../../components/CustomLoader/CustomLoader';
import UserDetailsDialog from '../../dialogs/UserDetailsDialog/UserDetailsDialog';
import { ReduxState } from '../../redux/types';
import User from '../../types/User';
import api from '../../utils/api';
import { useDialog } from '../../utils/dialog';
import snackbar from '../../utils/snackbar';
import { FlexContainer, Title } from './styled';

const apiPaginationItemsPerPage = 500;

const useStyles = makeStyles({
    root: {
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus': {
            outline: 'none',
        },
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
        '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus': {
            outline: 'none',
        },
    }
});

const UsersPage = () => {
    const classes = useStyles();
    const { t } = useTranslation();

    const isAdmin = useSelector((state: ReduxState) => state.user.type) === 'admin';
    const isCertifier = useSelector((state: ReduxState) => state.user.type) === 'certifier';

    const [isLoading, setIsLoading] = useState(true);
    const [users, setUsers] = useState<User[]>([]);
    const [selectedUserId, selectUserId] = useState(0);

    const [paginationPage, setPaginationPage] = useState(0);
    const [paginationItemsPerPage, setPaginationItemsPerPage] = useState(25);

    const [query, setQuery] = useState('');
    const [loadedQuery, setLoadedQuery] = useState('');
    const [loadedPage, setLoadedPage] = useState(0);
    const [loadedItemsPerPage, setLoadedItemsPerPage] = useState(25);
    const [internalLoading, setInternalLoading] = useState(false);
    const [debouncedQuery] = useDebounce(query, 500);

    const [isUserDialogOpen, setUserDialogOpen] = useState(false);

    const [openDialog, closeDialog] = useDialog();

    const apiRequestId = useRef('');

    const [anchor, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedUserIdMenu, setSelectedUserIdMenu] = useState<null | string>(null);

    const handleMenuClick = useCallback((event: React.MouseEvent<HTMLElement>, userId: string) => {
        setAnchorEl(event.currentTarget);
        setSelectedUserIdMenu(userId);
    }, []);

    const getData = useCallback(() => {
        const currentRequestId = uuid4();
        apiRequestId.current = currentRequestId;

        api.request('/users', 'GET', { query: debouncedQuery, page: paginationPage + 1, perPage: apiPaginationItemsPerPage }).then((res) => {
            if (currentRequestId !== apiRequestId.current) return;

            const { data, lastPage } = res;
            setUsers(data);
            setLoadedQuery(debouncedQuery);
            setLoadedPage(paginationPage);
            setLoadedItemsPerPage(apiPaginationItemsPerPage);

            if (paginationPage + 1 > lastPage) {
                setPaginationPage(lastPage - 1);
            }
        }).finally(() => {
            setIsLoading(false);
        });
    }, [paginationPage, debouncedQuery]);

    const deleteUser = useCallback((userId: number) => {
        api.request('/users/' + userId, 'DELETE').then(() => {
            snackbar.success(t('dialogs.user_delete.success'));
            getData();
        });
    }, [getData, t]);

    const deleteCompanyDialog = useCallback((userId: number) => {
        openDialog({
            title: t('dialogs.user_delete.title'),
            message: t('dialogs.user_delete.message'),
            actions: [
                {
                    text: t('dialogs.generic.cancel'),
                    color: 'secondary',
                    action: closeDialog
                },
                {
                    text: t('dialogs.generic.confirm'),
                    color: 'primary',
                    action: () => {
                        deleteUser(userId);
                        closeDialog();
                    }
                }
            ]
        });
    }, [closeDialog, deleteUser, openDialog, t]);

    const disableEnableUser = useCallback((userId: number, enabled: boolean) => {
        api.request('/users/' + userId + '/' + (enabled ? 'enable' : 'disable'), 'POST').then(() => {
            snackbar.success(t('pages.users.' + (enabled ? 'user_enabled_success' : 'user_disabled_success')));
            getData();
        });
    }, [getData, t]);

    const columns: GridColDef[] = useMemo(() => {
        return ([
            {
                field: 'surname',
                headerName: t('pages.users.table.surname'),
                sortable: true,
                flex: 1,
                minWidth: 120
            },
            {
                field: 'name',
                headerName: t('pages.users.table.name'),
                sortable: true,
                flex: 1,
                minWidth: 120
            },
            {
                field: 'email',
                headerName: t('pages.users.table.email'),
                sortable: true,
                flex: 1,
                minWidth: 120
            },
            {
                field: 'username',
                headerName: t('pages.users.table.username'),
                flex: 1,
                sortable: false,
                minWidth: 120
            },
            {
                field: 'phone_number',
                headerName: t('pages.users.table.phone_number'),
                flex: 1,
                sortable: false,
                minWidth: 120
            },
            {
                field: 'type',
                headerName: t('pages.users.table.type'),
                sortable: true,
                flex: 1,
                minWidth: 120
            },
            ...((isAdmin || isCertifier) ? [{
                field: 'enabled',
                headerName: t('pages.users.table.enabled'),
                sortable: true,
                width: 120,
                renderCell: (params: any) => (
                    <div style={{ backgroundColor: params.value ? undefined : '#f9d1cc', width: '100%', textAlign: 'center' }}>
                        {t('pages.generic.table.flag_' + (params.value ? 'yes' : 'no'))}
                    </div>
                )
            }] : []),
            {
                field: 'actions',
                headerName: t('pages.users.table.actions'),
                width: 100,
                sortable: false,
                renderCell: (params) => params.value
            }
        ]);
    }, [t, isAdmin, isCertifier]);

    const rows = useMemo(() => {
        return users.map((user: any) => {
            return ({
                ...user,
                type: t('user_types.' + user.type),
                actions: (
                    <div>
                        <IconButton onClick={(e: any) => handleMenuClick(e, user.id)} >
                            <MoreVertIcon />
                        </IconButton>
                        <Menu
                            anchorEl={anchor}
                            keepMounted
                            open={!!anchor && selectedUserIdMenu?.toString() === user.id?.toString()}
                            onClose={() => setAnchorEl(null)}
                        >
                            <MenuItem onClick={() => {
                                openUserDialog(user.id);
                                setAnchorEl(null);
                            }}>
                                {t('pages.users.table.details')}
                            </MenuItem>

                            {(isAdmin || isCertifier) && (
                                <MenuItem onClick={() => {
                                    disableEnableUser(user.id, !user.enabled);
                                    setAnchorEl(null);
                                }}>
                                    {t('pages.users.table.' + (user.enabled ? 'disable' : 'enable'))}
                                </MenuItem>
                            )}

                            {isAdmin && (
                                <MenuItem onClick={() => {
                                    deleteCompanyDialog(user.id);
                                    setAnchorEl(null);
                                }}>
                                    {t('pages.generic.table.delete')}
                                </MenuItem>
                            )}

                        </Menu>
                    </div>
                )
            });
        });
    }, [users, isAdmin, anchor, deleteCompanyDialog, disableEnableUser, handleMenuClick, selectedUserIdMenu, isCertifier, t]);

    useEffect(() => {
        setInternalLoading(query !== loadedQuery || paginationPage !== loadedPage || apiPaginationItemsPerPage !== loadedItemsPerPage);
    }, [query, loadedQuery, paginationPage, loadedPage, loadedItemsPerPage]);

    const openUserDialog = (companyId: number) => {
        selectUserId(companyId);
        setUserDialogOpen(true);
    };

    const closeUserDialog = useCallback(() => {
        setUserDialogOpen(false);
    }, [setUserDialogOpen]);

    useEffect(() => {
        if (!isUserDialogOpen) getData();
    }, [getData, isUserDialogOpen]);

    useEffect(() => {
        setPaginationPage(0);
    }, []);

    if (isLoading) {
        return <CustomLoader />;
    }

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '100%', maxHeight: '100%', height: '100%' }}>
                <FlexContainer>
                    <Title variant='h4' style={{ flex: 0 }}>{t('pages.users.title')}</Title>

                    <div style={{ flex: 1, flexGrow: 1, marginLeft: '24px', display: 'flex' }}>
                        <TextField
                            label='Ricerca utente'
                            variant='outlined'
                            placeholder='Ricerca per nome, cognome o indirizzo email'
                            value={query}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position='start'><SearchIcon /></InputAdornment>,
                            }}
                            style={{ width: '100%', maxWidth: '600px' }}
                            size='small'
                        />

                        {internalLoading && (
                            <div style={{ display: 'flex', alignItems: 'center', marginLeft: '16px' }}>
                                <CircularProgress />
                            </div>
                        )}
                    </div>

                    {(isAdmin || isCertifier) && <GreenButton variant='outlined' onClick={() => openUserDialog(0)}>
                        {t('pages.users.new_user')}
                    </GreenButton>}
                </FlexContainer>

                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                    <DataGrid
                        className={classes.root}
                        style={{ position: 'absolute', height: '100%', width: '100%', marginTop: '16px' }}
                        rows={rows}
                        columns={columns}
                        pageSize={paginationItemsPerPage}
                        onPageSizeChange={(val) => setPaginationItemsPerPage(val)}
                        disableSelectionOnClick
                        disableColumnMenu
                    />
                </div>
            </div >

            <UserDetailsDialog open={isUserDialogOpen} onClose={closeUserDialog} userId={selectedUserId} />
        </>
    );
};

export default UsersPage;
